import './style.modules.scss'

import { BrandIcon, BrandIconColor, BrandIconId } from '@ampcore/adel-react'
import { Spacer } from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import {
  AdvisorDataService,
  EmailPayloadTemplate,
  FaceProfileData
} from '@ampcore/client-side-services'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useEffect, useMemo, useState } from 'react'
import xss from 'xss'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { UserStatus } from '../../types/UserStatus.enum'
import { QuestionItem, ResultItem } from '../Assessment/Assessment'
import { EmailAdvisorModal } from '../EmailAdvisorModal/EmailAdvisorModal'

export type AssessmentResultProps = {
  questions: QuestionItem[]
  selectedAnswers: Record<number, number>
  caption: string
  results: ResultItem[]
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  componentSpacing?: SpacerSpacing
  showFacetBackground: boolean
  clientInquiryEmail: {
    subject?: string
    greeting?: string
    body?: string
  }
  disclosureText?: string
  overrideRecipient?: string
  analyticsName?: string
  psnAnalytics?: string
  onRetake?: () => void
  advisorData?: FaceProfileData
}

const riskLevels = [
  { label: 'Aggressive', color: 'aggressive' },
  { label: 'Moderately Aggressive', color: 'moderately-aggressive' },
  { label: 'Moderate', color: 'moderate' },
  { label: 'Moderately Conservative', color: 'moderately-conservative' },
  { label: 'Conservative', color: 'conservative' }
]
const advisorDataService = new AdvisorDataService()

export const AssessmentResult = ({
  questions,
  selectedAnswers,
  caption,
  results,
  icon,
  iconColor,
  fill,
  stroke,
  componentSpacing,
  showFacetBackground,
  disclosureText,
  overrideRecipient,
  clientInquiryEmail,
  analyticsName,
  psnAnalytics,
  onRetake
}: AssessmentResultProps) => {
  const getScores = () => {
    const scores: Record<string, number> = { __points: 0 }
    questions.forEach((q, qIndex) => {
      const selectedIndex = selectedAnswers[qIndex]
      const ans = q.answer[selectedIndex]
      const basePoints = parseInt(ans.points)
      scores.__points += basePoints

      if (ans.category) {
        const catPoints = parseInt(ans.categoryPoints)
        if (!scores[ans.category]) scores[ans.category] = 0
        scores[ans.category] += catPoints
      }
    })
    return scores
  }

  const scores = getScores()
  const assessmentResult = results.find(result => {
    const score = result.category ? scores[result.category] : scores.__points
    if (score === undefined) return false
    if (result.condition === 'greater') return score > Number(result.points)
    if (result.condition === 'equals') return score === Number(result.points)
    return false
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const closeModal = () => {
    setIsModalOpen(false)
  }
  const [advisorData, setAdvisorData] = useState<FaceProfileData | null>(null)
  const [isAdvisorDataLoading, setIsAdvisorDataLoading] = useState(false)
  const [isClient, setIsClient] = useState(false)

  const userStatus = useAmpUserStatus()
  useEffect(() => {
    const initClient = async () => {
      if (
        userStatus === UserStatus.CLIENT_ACTIVE ||
        userStatus === UserStatus.CLIENT_EXPIRED
      ) {
        setIsClient(true)
        setIsAdvisorDataLoading(true)
        try {
          const advisorRes = await advisorDataService.getByCookie()
          setAdvisorData(advisorRes.data)
        } catch (err) {
          console.error(err)
        } finally {
          setIsAdvisorDataLoading(false)
        }
      }
    }

    initClient()
  }, [userStatus])

  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('AssessmentResult')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )

  return (
    <Spacer spacing={componentSpacing}>
      <div className="AssessmentResult u-sizeConstrained u-flex">
        <div
          className="Card u-flexGrow1"
          style={
            showFacetBackground
              ? {
                  background: `linear-gradient(170deg, rgba(255, 255, 255, 1) 70%, rgba(255, 255, 255, 0) 135%), url(${window.AMPCOMPATH}images/facets/quiz/gray-white-facet.svg)`
                }
              : {}
          }
        >
          <div className="Card-body AssessmentResult-sectionWrapper u-sm-padding20 u-flex u-md-flexCol u-sm-flexCol">
            <div className="AssessmentResult-introSection u-flex u-flexCol u-flexJustifyCenter">
              {icon && (
                <BrandIcon
                  icon={icon}
                  color={iconColor ? iconColor : BrandIconColor.NONE}
                  fill={fill ? true : false}
                  stroke={stroke ? true : false}
                  className={`BrandIcon--sizeMedium`}
                />
              )}
              <h2 className="u-textColorNeutral u-textPrimary">
                Your risk tolerance results
              </h2>
              <div
                className="AssessmentResult-caption u-textColorNeutral u-textPrimary u-marginTop4 u-marginBottom20"
                dangerouslySetInnerHTML={{ __html: xss(caption) }}
              ></div>
            </div>
            <div className="AssessmentResult-timelineSection u-flexCol">
              <div className="Card Card--noShadow u-flex u-flexAlignItemsCenter u-lg-margin20">
                <div className="Card-body u-flex u-flexAlignItemsCenter u-sm-flexCol">
                  <div className="AssessmentResult-result u-padding4">
                    <h3 className="u-paddingRight32 u-textColorNeutral u-textPrimary">
                      {assessmentResult?.resultCategory}
                    </h3>
                    {assessmentResult?.content && (
                      <div
                        className="u-paddingRight32 u-marginTop4 u-textColorNeutral u-textPrimary"
                        dangerouslySetInnerHTML={{
                          __html: xss(assessmentResult.content)
                        }}
                      ></div>
                    )}
                  </div>

                  <div className="AssessmentResult-timelineWrapper u-flex u-flexCol u-flexAlignItemsCenter u-paddingVert32 u-md-paddingLeft32">
                    <div className="AssessmentResult-timeline u-flex u-flexCol">
                      {riskLevels.map((item, index) => (
                        <div
                          key={index}
                          className={`AssessmentResult-timelineItem AssessmentResult-timelineItem--${
                            item.color
                          } u-textBold u-textPrimary u-textSizeMsrm1 ${
                            item.label.toLowerCase() ===
                            assessmentResult?.resultCategory?.toLowerCase()
                              ? 'AssessmentResult-timelineItem--highlight'
                              : ''
                          }`}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="AssessmentResult-actionSection u-sm-flexCol u-flex u-paddingTop32 u-flexJustifyBetween">
                {isClient && advisorData?.email ? (
                  <div className="u-sm-marginBottom8">
                    <button
                      className="Button Button--primary"
                      onClick={() => setIsModalOpen(true)}
                      rel="noreferrer"
                      {...atb.dataAnalyticAttribute(
                        'email-results-to-your-advisor-button'
                      )}
                    >
                      Email results to your advisor
                    </button>
                    <EmailAdvisorModal
                      psnAnalytics={psnAnalytics}
                      isModalOpen={isModalOpen}
                      onClose={closeModal}
                      advisorInfo={{
                        name: advisorData.name!,
                        url: advisorData.urls.site!
                      }}
                      payload={{
                        subject: clientInquiryEmail.subject!,
                        greeting: clientInquiryEmail.greeting!,
                        message:
                          clientInquiryEmail.body! +
                          assessmentResult?.resultCategory,
                        recipient: advisorData.email,
                        template: EmailPayloadTemplate.CLIENT_INQUIRY,
                        overrideRecipient: overrideRecipient
                      }}
                      disclosureText={disclosureText}
                    />
                  </div>
                ) : (
                  <div className="u-sm-marginBottom8">
                    <a
                      className="Button Button--primary u-textNoWrap"
                      href="https://www.ameripriseadvisors.com"
                      target="_blank"
                      {...atb.dataAnalyticAttribute('find-advisor')}
                    >
                      {isAdvisorDataLoading ? 'Loading...' : 'Find an advisor'}
                    </a>
                  </div>
                )}
                <button
                  className="Button Button--secondary Button--secondary--action-blue"
                  onClick={onRetake}
                  {...atb.dataAnalyticAttribute(
                    'risk-tolerance-results-retake-button'
                  )}
                >
                  Retake quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Spacer>
  )
}
